import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectStore } from '@/stores';
import { Room } from '@/entities/room/model/types';

export function useRooms() {
  const { t } = useTranslation();

  // Narrow selectors to avoid subscribing to the entire store (perf + FSD isolation)
  const rawRooms = useProjectStore((s) => s.rooms);
  const meta = useProjectStore((s) => s.meta);
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const safetyMargin = useProjectStore((s) => s.safetyMargin);
  const activeRoomId = useProjectStore((s) => s.activeRoomId);
  const setActiveRoomId = useProjectStore((s) => s.setActiveRoomId);

  const deleteRoom = useProjectStore((s) => s.deleteRoom);
  const addRoom = useProjectStore((s) => s.addRoom);
  const loadProjectSnapshot = useProjectStore((s) => s.loadProjectSnapshot);
  const createProjectSnapshot = useProjectStore((s) => s.createProjectSnapshot);
  const updateMeta = useProjectStore((s) => s.updateMeta);
  const updateRoomDims = useProjectStore((s) => s.updateRoomDims);
  const updateRoomOpenings = useProjectStore((s) => s.updateRoomOpenings);
  const updateRoomPartitions = useProjectStore((s) => s.updateRoomPartitions);
  const updateRoomWallElements = useProjectStore((s) => s.updateRoomWallElements);
  const updateItem = useProjectStore((s) => s.updateItem);
  const removeItem = useProjectStore((s) => s.removeItem);
  const commitVersion = useProjectStore((s) => s.commitVersion);
  const resetRooms = useProjectStore((s) => s.resetRooms);
  const syncPrices = useProjectStore((s) => s.syncPrices);
  const duplicateRoom = useProjectStore((s) => s.duplicateRoom);
  const updateRoomName = useProjectStore((s) => s.updateRoomName);
  const updateRoom = useProjectStore((s) => s.updateRoom);
  const addServicesToRoom = useProjectStore((s) => s.addServicesToRoom);
  const setSafetyMargin = useProjectStore((s) => s.setSafetyMargin);

  // Global project layer (separation of global логика from rooms)
  const projectItems = useProjectStore((s) => s.projectItems);
  const projectOverrides = useProjectStore((s) => s.projectOverrides);
  const setProjectItems = useProjectStore((s) => s.setProjectItems);
  const setProjectOverrides = useProjectStore((s) => s.setProjectOverrides);
  const addProjectItem = useProjectStore((s) => s.addProjectItem);
  const updateProjectItem = useProjectStore((s) => s.updateProjectItem);
  const removeProjectItem = useProjectStore((s) => s.removeProjectItem);
  const addServicesToProject = useProjectStore((s) => s.addServicesToProject);

  const getRoomDisplayName = useCallback((room: Room, index: number) => {
    if (room.name?.trim()) return room.name;
    return t('editor.room_default_name', { n: index + 1, defaultValue: `Комната ${index + 1}` });
  }, [t]);

  const rooms = useMemo(() => {
    return rawRooms.map((room, idx) => ({
        ...room,
        displayName: getRoomDisplayName(room, idx)
    }));
  }, [rawRooms, getRoomDisplayName]);

  const removeRoom = useCallback((id: string) => {
      deleteRoom(id);
      if (activeRoomId === id) {
          const remaining = rooms.filter(r => r.id !== id);
          setActiveRoomId(remaining.length > 0 ? remaining[0].id : null);
      }
  }, [deleteRoom, activeRoomId, rooms, setActiveRoomId]);

  return {
    rooms,
    meta,
    updateMeta,
    activeProjectId,
    activeRoomId,
    setActiveRoomId,
    deleteRoom,
    removeRoom,
    addRoom,
    updateRoomDims,
    updateRoomOpenings,
    updateRoomPartitions,
    updateRoomWallElements,
    updateItem,
    removeItem,
    loadProjectSnapshot,
    createProjectSnapshot,
    commitVersion,
    resetRooms,
    syncPrices,
    duplicateRoom,
    updateRoomName,
    updateRoom,
    addServicesToRoom,
    safetyMargin,
    setSafetyMargin,
    // project global layer surfaced here for convenience during transition (deprecated hook path)
    projectItems,
    projectOverrides,
    setProjectItems,
    setProjectOverrides,
    addProjectItem,
    updateProjectItem,
    removeProjectItem,
    addServicesToProject
  };
}
